import { nodeConfig } from '@scripts/mixins';

const NAMESPACE = 'wysiwyg';
const DEFAULT_SELECTOR = '[data-js-wysiwyg]';

/**
 * @typedef {Object} WysiwygSettings
 * @property {string} [videoSelectors='iframe[src*="vimeo.com"], iframe[src*="youtube.com"]'] - Selectors for video embeds to wrap.
 */
const DEFAULT_SETTINGS = {
	videoSelectors: 'iframe[src*="vimeo.com"], iframe[src*="youtube.com"]'
};

/**
 * WYSIWYG content enhancements.
 */
class Wysiwyg {
	constructor(node, config) {
		this.elNode = node;
		this.config = config;
		this.tableObserver = null;
	}

	init() {
		this._wrapVideos();
		this._wrapTables();
		this._bindUI();
	}

	destroy() {
		if (this.tableObserver) {
			this.tableObserver.disconnect();
		}

		this._unbindUI();
	}

	_wrapVideos() {
		const videos = this.elNode.querySelectorAll(this.config.videoSelectors);

		videos.forEach((iframe) => {
			if (iframe.parentNode.classList.contains('video-frame')) return;

			const wrapper = document.createElement('div');
			wrapper.className = 'video-frame';

			iframe.removeAttribute('style');
			iframe.parentNode.insertBefore(wrapper, iframe);
			wrapper.appendChild(iframe);
		});
	}

	_wrapTables() {
		const tables = this.elNode.querySelectorAll('table');

		tables.forEach((table) => {
			if (table.closest('.table-wrapper')) return;

			const wrapper = document.createElement('div');
			wrapper.className = 'table-wrapper';

			const inner = document.createElement('div');
			inner.className = 'table-wrapper__inner';

			table.parentNode.insertBefore(wrapper, table);
			wrapper.appendChild(inner);
			inner.appendChild(table);

			this._checkOverflow(inner);
		});
	}

	/** @param {HTMLElement} inner */
	_checkOverflow(inner) {
		const wrapper = inner.parentElement;
		const scrollWidth = inner.scrollWidth;
		const clientWidth = inner.clientWidth;

		// 1px tolerance for sub-pixel rendering
		const isOverflowing = scrollWidth > clientWidth + 1;

		if (!isOverflowing) {
			wrapper.classList.remove(
				'table-wrapper--overflow',
				'table-wrapper--scroll-start',
				'table-wrapper--scroll-end'
			);
			inner.removeAttribute('tabindex');
			inner.removeAttribute('role');
			inner.removeAttribute('aria-label');

			return;
		}

		wrapper.classList.add('table-wrapper--overflow');
		inner.setAttribute('tabindex', '0');
		inner.setAttribute('role', 'group');
		inner.setAttribute('aria-label', 'Scrollable table');

		const scrollLeft = inner.scrollLeft;
		const atStart = scrollLeft <= 0;
		const atEnd = scrollLeft >= scrollWidth - clientWidth - 1;

		wrapper.classList.toggle('table-wrapper--scroll-start', !atStart);
		wrapper.classList.toggle('table-wrapper--scroll-end', !atEnd);
	}

	_bindUI() {
		this._onResize = this._onResize.bind(this);
		this._onScroll = this._onScroll.bind(this);

		const inners = this.elNode.querySelectorAll('.table-wrapper__inner');

		if (!inners.length) return;

		this.tableObserver = new ResizeObserver(this._onResize);

		inners.forEach((inner) => {
			this.tableObserver.observe(inner);
			inner.addEventListener('scroll', this._onScroll, { passive: true });
		});
	}

	_unbindUI() {
		const inners = this.elNode.querySelectorAll('.table-wrapper__inner');

		inners.forEach((inner) => {
			inner.removeEventListener('scroll', this._onScroll);
		});
	}

	/** @param {ResizeObserverEntry[]} entries */
	_onResize(entries) {
		entries.forEach((entry) => this._checkOverflow(entry.target));
	}

	_onScroll(event) {
		this._checkOverflow(event.target);
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new Wysiwyg(node, config);

		module.init();

		return module;
	});
}
